/* eslint-disable no-unused-vars */
import { useRef, useId } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { publisherReportsFiltersTableActions } from 'store/actions';
import styled from 'styled-components';
import { Palette } from 'config';
import { useSignal, useComputed } from '@preact/signals-react';
import { useRerender } from 'utils';
import { Virtuoso } from 'react-virtuoso';
import { Search, Buttons, ParagraphWithTooltip, MenuItem, CheckboxInner } from 'components';
import { Search as SmallSearchIcon } from 'assets/icons';

const getIcon = () => {
  return <SmallSearchIcon />;
};

const ROW_HEIGHT = 40;

export const Advertiser = ({ advertiserList$ }) => {
  const resetBtnId = useId();
  const applyBtnId = useId();
  const searchId = useId();
  const changingFlag$ = useSignal(false);
  const dispatch = useDispatch();
  const rerender = useRerender();
  const searchRef = useRef(null);
  const searchValue$ = useSignal('');
  const initialAdvertiserFilter = useSelector(
    (state) => state.publisherReportsFiltersTable.advertisersFilter,
  );
  const advertisersFilter$ = useSignal(new Set(initialAdvertiserFilter));

  const applyClick = () => {
    dispatch(
      publisherReportsFiltersTableActions.updateProperty({
        newValue: Array.from(advertisersFilter$.value),
        propField: 'advertisersFilter',
      }),
    );
    changingFlag$.value = false;
  };

  const resetClick = () => {
    changingFlag$.value = true;
    advertisersFilter$.value.clear();
    rerender();
  };

  const handleItemClick = (adveriser_id) => {
    advertisersFilter$.value.has(adveriser_id)
      ? advertisersFilter$.value.delete(adveriser_id)
      : advertisersFilter$.value.add(adveriser_id);
    changingFlag$.value = true;
    rerender();
  };

  const filteredAdvertiserList$ = useComputed(() => {
    const res = Array.from(advertiserList$.value.values()).filter(
      ({ advertiser, adveriser_id }) => {
        if (advertiser.toLowerCase().includes(searchValue$.value.toLowerCase())) return true;
        if (adveriser_id.toString().includes(searchValue$.value)) return true;
        return false;
      },
    );
    return res;
  });

  const sortedAdvertisersList$ = useComputed(() => {
    const filteredAdvertiser = filteredAdvertiserList$.value;
    const inSet = [];
    const notInSet = [];
    filteredAdvertiser.forEach((advertiser) => {
      if (advertisersFilter$.value.has(advertiser.adveriser_id)) {
        inSet.push(advertiser);
      } else {
        notInSet.push(advertiser);
      }
    });

    inSet.sort((a, b) => a.advertiser.localeCompare(b.advertiser));
    notInSet.sort((a, b) => a.advertiser.localeCompare(b.advertiser));

    return [...inSet, ...notInSet];
  });

  return (
    <Box>
      <SearchBox>
        <Search
          onFocus={() => {}}
          ref={searchRef}
          id={searchId}
          onChange={(e) => (searchValue$.value = e.target.value)}
          value={searchValue$?.value}
          placeholder='Search Advertiser'
          rightIconOnClick={(event) => {
            searchValue$.value = '';
          }}
          style={{ height: '48px' }}
          Icon={getIcon}
        />
      </SearchBox>

      <MyVirtuoso
        height={
          sortedAdvertisersList$.value.length >= 4
            ? 200
            : sortedAdvertisersList$.value.length * ROW_HEIGHT
        }
        data={sortedAdvertisersList$.value}
        itemContent={(index, { adveriser_id, advertiser }) => (
          <MenuItem
            wrapStyle={{
              display: 'flex',
              alignItems: 'center',
            }}
            isSelected={false}
            height={ROW_HEIGHT}
            value={adveriser_id}
            key={adveriser_id}
            onClick={handleItemClick}
          >
            <CheckboxInner
              readOnly={true}
              isChecked={advertisersFilter$.value.has(adveriser_id)}
              id={adveriser_id}
              name={advertiser}
              onChange={() => {}}
              style={{ marginRight: '16px' }}
            />
            <div
              style={{
                width: '147px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <ParagraphWithTooltip
                text={`${advertiser}`}
                search={searchValue$.value}
                isHighlight
                highlightStyle={{ color: `${Palette.Primary._500}`, fontWeight: '500' }}
              />
              <ParagraphWithTooltip
                text={`ID:${adveriser_id}`}
                search={searchValue$.value}
                style={{
                  color: `${Palette.PrimaryGray._700}`,
                  fontWeight: '400',
                  fontSize: '12px',
                }}
                isHighlight
                highlightStyle={{ color: `${Palette.Primary._500}`, fontWeight: '500' }}
              />
            </div>
          </MenuItem>
        )}
      />
      <Footer>
        <Buttons.SecondarySmall
          id={resetBtnId}
          isDisabled={!advertisersFilter$.value.size}
          onClick={resetClick}
        >
          Reset
        </Buttons.SecondarySmall>
        <Buttons.PrimarySmall
          id={applyBtnId}
          isDisabled={!changingFlag$.value}
          onClick={applyClick}
        >
          Apply
        </Buttons.PrimarySmall>
      </Footer>
    </Box>
  );
};

const MyVirtuoso = styled(Virtuoso)`
  width: 100% !important;
  height: ${({ height }) => `${height + 6}px!important`};

  > div {
    width: 100% !important;
    padding: 3px !important;
  }

  overscroll-behavior: none !important;
  ::-webkit-scrollbar {
    width: 6px;
    background-color: #ffffff;
  }
  ::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background-color: ${() => Palette.PrimaryGray._300};
  }
  ::-webkit-scrollbar-track {
    border-radius: 10px;
    background-color: #ffffff;
  }
`;

const Box = styled.div`
  width: 100%;
  height: auto;
`;

const Footer = styled.div`
  display: flex;
  height: 64px;
  gap: 16px;
  justify-content: end;
  background: ${Palette.PrimaryGray._000};
  border-top: 1px solid ${Palette.PrimaryGray._200};
  border-bottom-right-radius: 4px;
  padding: 16px 24px;
`;

const SearchBox = styled.div`
  padding: 0 16px;
  border-bottom: 1px solid ${Palette.PrimaryGray._200};
  width: 100%;
`;
